import {createSvgEl} from "../RouteTechniques/Util/Svg.js";
import {RandomNumberGenerator} from "../Util/RandomNumberGenerator.js";

enum RaamschriftOrientation {
    TOP = 0,
    LEFT = 1,
    DOWN = 2,
    RIGHT = 3,
}

export class Raamschrift {
    readonly odd: boolean;
    readonly max_length: number;
    readonly distribution: number[][];
    readonly m: number;
    readonly n: number;

    public static computeMinSizeForText(text: string): number {
        const size = Math.ceil(Math.sqrt(text.length));

        if (size * size === text.length && size % 2 === 1) {
            return size + 1;
        }

        return size;
    }

    public constructor(readonly size: number, readonly seed: number|null = null) {
        if (this.seed === null) {
            this.seed = Math.random();
        }

        const rng = new RandomNumberGenerator(seed);

        this.odd = this.size % 2 === 1;

        // In odd sizes, the center square cannot participate
        this.max_length = this.size ** 2 - (this.odd ? 1 : 0);

        this.distribution = [];
        this.m = Math.floor(this.size / 2); // Vertical size
        this.n = Math.ceil(this.size / 2); // Horizontal size
        for (let p = 0; p < this.m; p++) {
            this.distribution.push([]);
            for (let q = 0; q < this.n; q++) {
                this.distribution[p].push(rng.nextBetween(0, 4));
            }
        }
    }

    public isDifficultToCut(): boolean {
        const crossGrid = this.computeCrossGrid(RaamschriftOrientation.TOP);

        for (let i = 1; i < this.size - 1; i++) {
            for (let j = 1; j < this.size - 1; j++) {
                if (
                    !crossGrid[i][j]
                    && crossGrid[i-1][j]
                    && crossGrid[i+1][j]
                    && crossGrid[i][j-1]
                    && crossGrid[i][j+1]
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    public distributionValueToGridIndex(orientation: RaamschriftOrientation, p: number, q: number): [number, number] {
        const value = this.distribution[p][q];

        let offset = null;
        if (orientation === RaamschriftOrientation.TOP) {
            offset = 0;
        } else if (orientation === RaamschriftOrientation.LEFT) {
            offset = 1;
        } else if (orientation === RaamschriftOrientation.DOWN) {
            offset = 2;
        } else if (orientation === RaamschriftOrientation.RIGHT) {
            offset = 3;
        } else {
            throw new Error();
        }

        const offsetValue = (value + 4 - offset) % 4;

        if (offsetValue === 0) {
            return [p, q];
        } else if (offsetValue === 1) {
            return [q, this.size - 1 - p];
        } else if (offsetValue === 2) {
            return [this.size - 1 - p, this.size - 1 - q];
        } else if (offsetValue === 3) {
            return [this.size - 1 - q, p];
        } else {
            throw new Error();
        }
    }

    private buildGrid<T>(value: T): T[][] {
        const grid = [];
        for (let i = 0; i < this.size; i++) {
            grid.push([]);
            for (let j = 0; j < this.size; j++) {
                grid[i].push(value);
            }
        }
        return grid;
    }

    public computeCrossGrid(orientation: RaamschriftOrientation): boolean[][] {
        const crossGrid = this.buildGrid(false);

        for (let p = 0; p < this.m; p++) {
            for (let q = 0; q < this.n; q++) {
                const gridIndex = this.distributionValueToGridIndex(orientation, p, q);
                crossGrid[gridIndex[0]][gridIndex[1]] = true;
            }
        }

        return crossGrid;
    }

    public computeMessageGrid(text): string[][] {
        if (text.length > this.max_length) {
            throw new Error();
        }

        const messageGrid = this.buildGrid<string|null>(null);
        let textCursor = 0;
        for (const orientation of [
            RaamschriftOrientation.TOP,
            RaamschriftOrientation.LEFT,
            RaamschriftOrientation.DOWN,
            RaamschriftOrientation.RIGHT,
        ]) {
            const crossGrid = this.computeCrossGrid(orientation);

            for (let i = 0; i < this.size; i++) {
                for (let j = 0; j < this.size; j++) {
                    if (crossGrid[i][j]) {
                        if (textCursor < text.length) {
                            messageGrid[i][j] = text[textCursor];
                            textCursor++;
                        } else {
                            messageGrid[i][j] = '';
                        }
                    }
                }
            }
        }

        return messageGrid;
    }

    private generateGridSvg(): SVGSVGElement {
        const svg = createSvgEl('svg', {
            width: this.size,
            height: this.size,
            viewBox: "0 0 " + this.size + " " + this.size,
        });

        for (let i = 0; i <= this.size; i++) {
            svg.appendChild(createSvgEl('line', {
                x1: 0,
                y1: i,
                x2: this.size,
                y2: i,
                stroke: '#000000',
                strokeWidth: 0.05,
            }));

            svg.appendChild(createSvgEl('line', {
                x1: i,
                y1: 0,
                x2: i,
                y2: this.size,
                stroke: '#000000',
                strokeWidth: 0.05,
            }));
        }

        return svg;
    }

    public generateCrossGridSvg(): SVGSVGElement {
        const svg = this.generateGridSvg();

        const crossGrid = this.computeCrossGrid(RaamschriftOrientation.TOP);

        for (let i = 0; i < this.size; i++) {
            for (let j = 0; j < this.size; j++) {
                if (crossGrid[i][j]) {
                    svg.appendChild(createSvgEl('line', {
                        x1: j + 0.2,
                        y1: i + 0.2,
                        x2: j + 0.8,
                        y2: i + 0.8,
                        stroke: '#000000',
                        strokeWidth: 0.05,
                    }));
                    svg.appendChild(createSvgEl('line', {
                        x1: j + 0.8,
                        y1: i + 0.2,
                        x2: j + 0.2,
                        y2: i + 0.8,
                        stroke: '#000000',
                        strokeWidth: 0.05,
                    }));
                }
            }
        }
        return svg;
    }

    public generateMessageGridSvg(text: string): SVGSVGElement {
        const svg = this.generateGridSvg();

        const messageGrid = this.computeMessageGrid(text);

        for (let i = 0; i < this.size; i++) {
            for (let j = 0; j < this.size; j++) {
                const text = createSvgEl('text', {
                    x: j + 0.5,
                    y: i + 0.5,
                    textAnchor: 'middle',
                    dominantBaseline: 'central',
                });
                text.textContent = messageGrid[i][j];
                text.style.font = '.8px sans-serif';
                svg.appendChild(text);
            }
        }

        return svg;
    }
}
